import React from 'react';
import { FileText } from 'lucide-react';
import { Contract, TableColumn } from '@app-types';
import Badge from '@components/common/Badge';
import { contractStatusToVariant } from '@utils/badgeMappers';
import { RZTable } from '@components/common/table';
import { InfoCard, ContractName } from '@components/common';
import { LoadingState } from '@components/common/loading';
import { formatCalendarDate } from '@utils/dateUtils';
import styles from './SupplierContracts.module.css';

interface SupplierContractsProps {
  contracts: Contract[];
  isLoading?: boolean;
  onViewContract?: (contractId: number) => void;
  onEditContract?: (contractId: number) => void;
  canEdit?: boolean;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
  };
}

// Column configuration
const supplierContractColumns: TableColumn[] = [
  { key: 'contract_number', label: 'Contract Number', defaultVisible: true, alwaysVisible: true },
  { key: 'name', label: 'Contract Name', defaultVisible: true },
  { key: 'value', label: 'Value', defaultVisible: true },
  { key: 'start_date', label: 'Start Date', defaultVisible: true },
  { key: 'end_date', label: 'End Date', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'owner', label: 'Contract Owner', defaultVisible: true },
  { key: 'manager', label: 'Contract Manager', defaultVisible: true }
];

const SupplierContracts: React.FC<SupplierContractsProps> = ({
  contracts,
  isLoading = false,
  onViewContract,
  onEditContract,
  canEdit = false,
  pagination
}) => {
  // start_date / end_date are @db.Date calendar days — no timezone conversion.
  const formatDate = (dateString: string | null | undefined) => {
    return formatCalendarDate(dateString, '—');
  };

  const formatCurrency = (value?: number) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

    const formatAssignees = (
    assignees: Array<{ id: number; name: string }> | undefined,
    fallbackName: string | undefined
  ) => {
    if (assignees?.length) {
      const displayText =
        assignees.length === 1
          ? assignees[0].name
          : `${assignees[0].name} +${assignees.length - 1}`;
      const titleText = assignees.map(a => a.name).join(', ');
      return (
        <span className={styles.assigneeCell} title={titleText}>
          {displayText}
        </span>
      );
    }
    return (
      <span className={styles.unassigned}>
        {fallbackName || 'Unassigned'}
      </span>
    );
  };

  // Cell rendering logic
  const renderCell = (column: TableColumn, contract: Contract) => {
    switch (column.key) {
      case 'contract_number':
        return (
          <button className="table-link" onClick={() => onViewContract?.(contract.id)}>
            {contract.contract_number}
          </button>
        );

      case 'name':
        return <ContractName name={contract.name} description={contract.description} />;

      case 'value':
        return formatCurrency(contract.amount);

      case 'start_date':
        return formatDate(contract.start_date);

      case 'end_date':
        return formatDate(contract.end_date);

      case 'status':
        return <Badge variant={contractStatusToVariant(contract.status)}>{contract.status}</Badge>;
      case 'owner':
        return formatAssignees(contract.contract_owners, contract.contract_owner_name);

      case 'manager':
        return formatAssignees(contract.contract_managers, contract.contract_manager_name);

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <InfoCard>
        <InfoCard.Header>
          <InfoCard.Title icon={<FileText size={18} />}>Contracts</InfoCard.Title>
        </InfoCard.Header>
        <InfoCard.Content>
          <LoadingState message="Loading contracts..." />
        </InfoCard.Content>
      </InfoCard>
    );
  }

  return (
    <InfoCard>
      <InfoCard.Header>
        <InfoCard.Title icon={<FileText size={18} />}>
          Contracts {pagination ? `(${pagination.totalItems})` : `(${contracts.length})`}
        </InfoCard.Title>
      </InfoCard.Header>

      <InfoCard.Content>
        <RZTable
          data={contracts}
          columns={supplierContractColumns}
          getRowKey={(contract) => contract.id}
          renderCell={renderCell}
          pagination={
            pagination
              ? {
                  ...pagination,
                  itemName: 'contracts',
                  pageSizeOptions: [25, 50, 100]
                }
              : undefined
          }
          state={{
            isLoading: false,
            isEmpty: contracts.length === 0,
            emptyTitle: 'No contracts found',
            emptyDescription: 'No contracts found for this supplier.',
            emptyType: 'contracts'
          }}
        />
      </InfoCard.Content>
    </InfoCard>
  );
};

export default SupplierContracts;
